import React, { useEffect, useState, useRef } from 'react'
import { getOptimizedImageUrl } from '../lib/imageHelper'

export default function Lightbox({ images = [], activeIndex = 0, onClose, onSelectIndex }) {
  const [touchStartX, setTouchStartX] = useState(null)
  const currentImage = images[activeIndex]

  useEffect(() => {
    function handleKeyDown(e) {
      if (e.key === 'Escape') {
        onClose()
      } else if (e.key === 'ArrowLeft') {
        goToPrev()
      } else if (e.key === 'ArrowRight') {
        goToNext()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    document.body.style.overflow = 'hidden'

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = ''
    }
  }, [activeIndex, images.length])

  if (!images || images.length === 0 || activeIndex === null || activeIndex === undefined) {
    return null
  }

  function goToNext() {
    if (images.length <= 1) return
    const next = (activeIndex + 1) % images.length
    onSelectIndex(next)
  }

  function goToPrev() {
    if (images.length <= 1) return
    const prev = (activeIndex - 1 + images.length) % images.length
    onSelectIndex(prev)
  }

  function handleTouchStart(e) {
    setTouchStartX(e.touches[0].clientX)
  }

  function handleTouchEnd(e) {
    if (touchStartX === null) return
    const touchEndX = e.changedTouches[0].clientX
    const diff = touchStartX - touchEndX
    if (diff > 50) {
      goToNext()
    } else if (diff < -50) {
      goToPrev()
    }
    setTouchStartX(null)
  }

  const imageUrl = currentImage?.image || currentImage?.url || currentImage
  const imageTitle = currentImage?.title || 'JT Knudsen projektbillede'
  const imageCaption = currentImage?.caption || currentImage?.description || ''

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-stone-950/95 backdrop-blur-md p-4 sm:p-6 select-none"
      onClick={onClose}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      role="dialog"
      aria-modal="true"
      aria-label="Billedvisning"
    >
      {/* Top action bar */}
      <div
        className="absolute top-4 left-4 right-4 flex items-center justify-between text-stone-200 z-10"
        onClick={(e) => e.stopPropagation()}
      >
        <span className="text-xs sm:text-sm font-medium bg-stone-900/80 border border-stone-800 px-3 py-1.5 rounded-full">
          {activeIndex + 1} af {images.length}
        </span>

        <button
          type="button"
          onClick={onClose}
          className="w-10 h-10 rounded-full bg-stone-900/80 hover:bg-stone-800 border border-stone-700 text-stone-200 hover:text-white flex items-center justify-center transition-colors cursor-pointer"
          aria-label="Luk visning"
        >
          <i className="fa-solid fa-xmark text-lg" aria-hidden="true"></i>
        </button>
      </div>

      {/* Prev / Next buttons */}
      {images.length > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              goToPrev()
            }}
            className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 w-11 h-11 sm:w-13 sm:h-13 rounded-full bg-stone-900/80 hover:bg-stone-800 border border-stone-700 text-stone-200 hover:text-white flex items-center justify-center transition-colors cursor-pointer z-10"
            aria-label="Forrige billede"
          >
            <i className="fa-solid fa-chevron-left text-lg" aria-hidden="true"></i>
          </button>

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              goToNext()
            }}
            className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 w-11 h-11 sm:w-13 sm:h-13 rounded-full bg-stone-900/80 hover:bg-stone-800 border border-stone-700 text-stone-200 hover:text-white flex items-center justify-center transition-colors cursor-pointer z-10"
            aria-label="Næste billede"
          >
            <i className="fa-solid fa-chevron-right text-lg" aria-hidden="true"></i>
          </button>
        </>
      )}

      {/* Main Image content */}
      <div
        className="max-w-5xl max-h-[85vh] flex flex-col items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={getOptimizedImageUrl(imageUrl, { width: 1400, quality: 85 })}
          alt={imageTitle ? `${imageTitle} - JT Knudsen` : 'Udført anlægs- og gravearbejde af JT Knudsen'}
          onError={(e) => {
            if (e.currentTarget.src !== imageUrl) {
              e.currentTarget.src = imageUrl
            }
          }}
          className="max-h-[70vh] sm:max-h-[75vh] w-auto max-w-full rounded-lg shadow-2xl object-contain bg-stone-900"
        />

        {(imageTitle || imageCaption) && (
          <div className="mt-4 text-center max-w-xl px-4">
            <h4 className="text-base sm:text-lg font-bold text-stone-100">
              {imageTitle}
            </h4>
            {imageCaption && (
              <p className="mt-1 text-xs sm:text-sm text-stone-400">
                {imageCaption}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
